'use client';
import React, { PropsWithChildren } from 'react';

import Link, { LinkProps } from 'next/link';
import { usePathname } from 'next/navigation';

/**
 * Plain modifier-free primary click — the only kind that should navigate in
 * place. Cmd/ctrl/shift/middle clicks keep their browser meaning (new tab…).
 */
function isPlainClick(e: React.MouseEvent): boolean {
  return e.button === 0 && !e.metaKey && !e.ctrlKey && !e.shiftKey && !e.altKey;
}

function NavLink(
  props: PropsWithChildren<LinkProps & { className?: string; target?: string }>
) {
  const asPath = usePathname();
  const string =
    typeof props.href === 'object' ? props.href.href || '' : props.href;
  const active = asPath === props.href;
  const parent = asPath?.startsWith(string);

  // Clicking the link for the page you're already on does a real reload, not
  // Next's soft re-navigation. For a same-URL push the pages router re-fetches
  // the route data and, when that fails — stale build id after a deploy
  // (404), or the request aborted by an outbound navigation — falls back to a
  // hard navigation it then refuses because the URL is unchanged:
  // "Invariant: attempted to hard navigate to the same URL" (Rollbar
  // onark-frontend #478/#484). The user's click did nothing; the reload is
  // what Next was trying to do.
  const onClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    props.onClick?.(e);
    if (e.defaultPrevented || !active || props.target || !isPlainClick(e)) {
      return;
    }
    e.preventDefault();
    window.location.assign(string);
  };

  return (
    <Link
      {...props}
      data-active={active}
      data-parent={parent}
      onClick={onClick}
    >
      {props.children}
    </Link>
  );
}

export default NavLink;
